import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Building2, Phone, Mail, MapPin, Edit, FileText, Calendar,
  Plus, Upload, Trash2, User, Clock, Euro, Lightbulb, UserPlus, Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRMCustomerDetail = ({ user, onLogout }) => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Communication dialog
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [editingComm, setEditingComm] = useState(null);
  const [commForm, setCommForm] = useState({
    communication_type: 'call',
    outcome: '',
    notes: '',
    next_contact_date: '',
    estimated_budget: '',
    business_idea: ''
  });
  
  // Assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningComm, setAssigningComm] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  
  // Edit customer dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Region autocomplete
  const [regionSearch, setRegionSearch] = useState('');
  const [showRegionSuggestions, setShowRegionSuggestions] = useState(false);
  
  // File upload
  const [uploading, setUploading] = useState(false);

  // Collaborators
  const [collabDialogOpen, setCollabDialogOpen] = useState(false);
  const [editingCollabIdx, setEditingCollabIdx] = useState(null);
  const [collabForm, setCollabForm] = useState({ name: '', role: '', phone: '', email: '' });

  useEffect(() => {
    if (customerId) {
      fetchCustomer();
      fetchCommunications();
      fetchFiles();
      fetchRegions();
      fetchPrograms();
      fetchCategories();
      if (user.role === 'owner') {
        fetchTeamMembers();
      }
    }
  }, [customerId]);

  // KAD file state
  const [kadFile, setKadFile] = useState(null);
  const [kadUploading, setKadUploading] = useState(false);

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/crm/customers/${customerId}?user_id=${user.user_id}`);
      setCustomer(response.data);
      setEditForm(response.data);
      // Fetch KAD file if exists
      if (response.data.kad_file_id) {
        fetchKadFile();
      }
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης πελάτη');
      navigate('/crm');
    } finally {
      setLoading(false);
    }
  };

  const fetchKadFile = async () => {
    try {
      const response = await axios.get(`${API}/crm/customers/${customerId}/kad-file?user_id=${user.user_id}`);
      setKadFile(response.data);
    } catch (error) {
      // No KAD file - that's ok
      setKadFile(null);
    }
  };

  const handleKadUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setKadUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/crm/customers/${customerId}/kad-file?user_id=${user.user_id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Το αρχείο ΚΑΔ ανέβηκε');
      fetchKadFile();
      fetchCustomer();
    } catch (error) {
      toast.error('Σφάλμα ανεβάσματος αρχείου');
    } finally {
      setKadUploading(false);
    }
  };

  const handleKadDownload = async () => {
    try {
      const response = await axios.get(
        `${API}/crm/customers/${customerId}/kad-file/download?user_id=${user.user_id}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', kadFile?.filename || 'kad_file');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Σφάλμα λήψης αρχείου');
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(
        `${API}/crm/files/${fileId}/download?user_id=${user.user_id}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'file');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Σφάλμα λήψης αρχείου');
    }
  };

  const handleKadDelete = async () => {
    try {
      await axios.delete(`${API}/crm/customers/${customerId}/kad-file?user_id=${user.user_id}`);
      toast.success('Το αρχείο ΚΑΔ διαγράφηκε');
      setKadFile(null);
      fetchCustomer();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής αρχείου');
    }
  };

  const fetchCommunications = async () => {
    try {
      const response = await axios.get(`${API}/crm/customers/${customerId}/communications?user_id=${user.user_id}`);
      setCommunications(response.data);
    } catch (error) {
      console.error('Error fetching communications:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/crm/customers/${customerId}/files?user_id=${user.user_id}`);
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API}/crm/regions?user_id=${user.user_id}`);
      setRegions(response.data);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/crm/programs?user_id=${user.user_id}`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/crm/categories?user_id=${user.user_id}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members?user_id=${user.user_id}`);
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // Assign communication to team member
  const openAssignDialog = (comm) => {
    setAssigningComm(comm);
    setSelectedAssignee(comm.employee_id || '');
    setAssignDialogOpen(true);
  };

  const handleAssignCommunication = async () => {
    if (!assigningComm || !selectedAssignee) return;
    
    try {
      await axios.put(
        `${API}/crm/communications/${assigningComm.communication_id}/assign?user_id=${user.user_id}&assigned_to=${selectedAssignee}`
      );
      toast.success('Η επικοινωνία ανατέθηκε');
      setAssignDialogOpen(false);
      setAssigningComm(null);
      fetchCommunications();
    } catch (error) {
      toast.error('Σφάλμα ανάθεσης');
    }
  };

  // Filter regions based on search
  const filteredRegions = useMemo(() => {
    if (!regionSearch) return regions;
    return regions.filter(r => 
      r.name.toLowerCase().includes(regionSearch.toLowerCase())
    );
  }, [regions, regionSearch]);

  const selectRegion = (region) => {
    setEditForm(prev => ({ ...prev, region_id: region.region_id }));
    setRegionSearch(region.name);
    setShowRegionSuggestions(false);
  };

  // Validate ΑΦΜ
  const validateTaxId = (value) => {
    return value.replace(/\D/g, '').slice(0, 9);
  };

  const openNewCommunicationDialog = () => {
    setEditingComm(null);
    setCommForm({
      communication_type: 'call',
      outcome: '',
      notes: '',
      next_contact_date: '',
      estimated_budget: '',
      business_idea: '',
      program_interest: ''
    });
    setCommDialogOpen(true);
  };

  const openEditCommunicationDialog = (comm) => {
    setEditingComm(comm);
    setCommForm({
      communication_type: comm.communication_type || 'call',
      outcome: comm.outcome || '',
      notes: comm.notes || '',
      next_contact_date: comm.next_contact_date || '',
      estimated_budget: comm.estimated_budget || '',
      business_idea: comm.business_idea || '',
      program_interest: comm.program_interest || ''
    });
    setCommDialogOpen(true);
  };

  const handleSaveCommunication = async (e) => {
    e.preventDefault();
    
    if (!commForm.outcome.trim()) {
      toast.error('Το αποτέλεσμα είναι υποχρεωτικό');
      return;
    }

    // Date is required for any appointment-creating communication
    // (i.e., whenever interest_status is NOT 'not_interested')
    if (commForm.interest_status !== 'not_interested' && !commForm.next_contact_date) {
      toast.error('Η ημερομηνία ραντεβού είναι υποχρεωτική. Παρακαλώ συμπληρώστε ημερομηνία.');
      return;
    }

    // Check for open appointments when scheduling a new one (not editing)
    if (!editingComm && commForm.next_contact_date) {
      try {
        const openRes = await axios.get(
          `${API}/crm/customers/${customerId}/open-appointments?user_id=${user.user_id}`
        );
        if (openRes.data.count > 0) {
          const confirmed = window.confirm(
            `Υπάρχει ήδη ανοιχτή πώληση σε εξέλιξη (${openRes.data.count} ραντεβού).\n\nΕίστε σίγουροι ότι επιθυμείτε να συνεχίσετε σε νέα επικοινωνία;`
          );
          if (!confirmed) return;
        }
      } catch (err) {
        // If check fails, continue
      }
    }
    
    try {
      const data = {
        ...commForm,
        estimated_budget: commForm.estimated_budget ? parseFloat(commForm.estimated_budget) : null
      };

      if (editingComm) {
        // Update existing communication
        await axios.put(`${API}/crm/communications/${editingComm.communication_id}?user_id=${user.user_id}`, data);
        toast.success('Η επικοινωνία ενημερώθηκε');
      } else {
        // Create new communication
        await axios.post(`${API}/crm/communications?user_id=${user.user_id}`, {
          customer_id: customerId,
          ...data
        });
        toast.success('Η επικοινωνία καταχωρήθηκε' + (commForm.next_contact_date ? ' και δημιουργήθηκε ραντεβού' : ''));
      }
      
      setCommDialogOpen(false);
      setEditingComm(null);
      fetchCommunications();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    
    // Validate ΑΦΜ
    if (editForm.tax_id && editForm.tax_id.length !== 9) {
      toast.error('Το ΑΦΜ πρέπει να είναι 9 ψηφία');
      return;
    }
    
    try {
      const updateData = {
        ...editForm,
        eme: editForm.eme ? parseFloat(editForm.eme) : null,
        annual_revenue: editForm.annual_revenue ? parseFloat(editForm.annual_revenue) : null,
        estimated_budget: editForm.estimated_budget ? parseFloat(editForm.estimated_budget) : null
      };
      
      await axios.put(`${API}/crm/customers/${customerId}?user_id=${user.user_id}`, updateData);
      toast.success('Ο πελάτης ενημερώθηκε');
      setEditDialogOpen(false);
      fetchCustomer();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/crm/customers/${customerId}/files?user_id=${user.user_id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Το αρχείο ανέβηκε');
      fetchFiles();
    } catch (error) {
      toast.error('Σφάλμα ανεβάσματος');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Διαγραφή αρχείου;')) return;
    
    try {
      await axios.delete(`${API}/crm/files/${fileId}?user_id=${user.user_id}`);
      toast.success('Το αρχείο διαγράφηκε');
      fetchFiles();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const toggleProgramInterest = (programId) => {
    setEditForm(prev => {
      const interests = prev.program_interests || [];
      if (interests.includes(programId)) {
        return { ...prev, program_interests: interests.filter(id => id !== programId) };
      } else {
        return { ...prev, program_interests: [...interests, programId] };
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('el-GR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('el-GR');
    } catch {
      return dateStr;
    }
  };

  // Collaborator handlers
  const handleSaveCollaborator = async () => {
    if (!collabForm.name.trim()) { toast.error('Το όνομα είναι υποχρεωτικό'); return; }
    const collabs = [...(customer.collaborators || [])];
    if (editingCollabIdx !== null) {
      collabs[editingCollabIdx] = { ...collabForm };
    } else {
      collabs.push({ ...collabForm });
    }
    try {
      await axios.put(`${API}/crm/customers/${customerId}?user_id=${user.user_id}`, { collaborators: collabs });
      setCustomer({ ...customer, collaborators: collabs });
      setCollabDialogOpen(false);
      setCollabForm({ name: '', role: '', phone: '', email: '' });
      setEditingCollabIdx(null);
      toast.success(editingCollabIdx !== null ? 'Ο συνεργάτης ενημερώθηκε' : 'Ο συνεργάτης προστέθηκε');
    } catch { toast.error('Σφάλμα αποθήκευσης'); }
  };

  const handleDeleteCollaborator = async (idx) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε αυτόν τον συνεργάτη;')) return;
    const collabs = (customer.collaborators || []).filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/crm/customers/${customerId}?user_id=${user.user_id}`, { collaborators: collabs });
      setCustomer({ ...customer, collaborators: collabs });
      toast.success('Ο συνεργάτης διαγράφηκε');
    } catch { toast.error('Σφάλμα διαγραφής'); }
  };

  const openCollabDialog = (idx = null) => {
    if (idx !== null) {
      setCollabForm({ ...customer.collaborators[idx] });
      setEditingCollabIdx(idx);
    } else {
      setCollabForm({ name: '', role: '', phone: '', email: '' });
      setEditingCollabIdx(null);
    }
    setCollabDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Φόρτωση...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Ο πελάτης δεν βρέθηκε</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/crm')}
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Πίσω
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-slate-400" />
                  {customer.company_name}
                </h1>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    customer.customer_type === 'existing' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {customer.customer_type === 'existing' ? 'Υφιστάμενη Επιχείρηση' : 'Υπό Σύσταση'}
                  </span>
                  {customer.tax_id && (
                    <span className="text-sm text-slate-500">ΑΦΜ: {customer.tax_id}</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setEditForm(customer); setRegionSearch(customer.region_name || ''); setEditDialogOpen(true); }}
                data-testid="edit-customer-button"
              >
                <Edit className="h-4 w-4 mr-2" />
                Επεξεργασία
              </Button>
              <Button
                onClick={openNewCommunicationDialog}
                data-testid="add-communication-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Νέα Επικοινωνία
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="info" className="space-y-4">
          <TabsList>
            <TabsTrigger value="info" data-testid="tab-info">Στοιχεία</TabsTrigger>
            <TabsTrigger value="communications" data-testid="tab-communications">
              Επικοινωνίες ({communications.length})
            </TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files">
              Αρχεία ({files.length})
            </TabsTrigger>
          </TabsList>

          {/* Info Tab */}
          <TabsContent value="info" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Contact Info */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-semibold text-slate-700 mb-4">Στοιχεία Επικοινωνίας</h3>
                <div className="space-y-3">
                  {customer.contact_person && (
                    <div className="flex items-start gap-3">
                      <User className="h-4 w-4 text-slate-400 mt-1" />
                      <div>
                        <div className="text-xs text-slate-500">Υπεύθυνος</div>
                        <div className="text-slate-900">{customer.contact_person}</div>
                      </div>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-start gap-3">
                      <Mail className="h-4 w-4 text-slate-400 mt-1" />
                      <div>
                        <div className="text-xs text-slate-500">Email</div>
                        <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline">
                          {customer.email}
                        </a>
                      </div>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 text-slate-400 mt-1" />
                      <div>
                        <div className="text-xs text-slate-500">Τηλέφωνο</div>
                        <a href={`tel:${customer.phone}`} className="text-slate-900">{customer.phone}</a>
                      </div>
                    </div>
                  )}
                  {customer.mobile && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 text-slate-400 mt-1" />
                      <div>
                        <div className="text-xs text-slate-500">Κινητό</div>
                        <a href={`tel:${customer.mobile}`} className="text-slate-900">{customer.mobile}</a>
                      </div>
                    </div>
                  )}
                  {customer.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-slate-400 mt-1" />
                      <div>
                        <div className="text-xs text-slate-500">Διεύθυνση</div>
                        <div className="text-slate-900">{customer.address}</div>
                      </div>
                    </div>
                  )}
                  {customer.region_name && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-slate-400 mt-1" />
                      <div>
                        <div className="text-xs text-slate-500">Περιφέρεια</div>
                        <div className="text-slate-900">{customer.region_name}</div>
                      </div>
                    </div>
                  )}
                  {customer.tax_id && (
                    <div className="flex items-start gap-3">
                      <FileText className="h-4 w-4 text-slate-400 mt-1" />
                      <div>
                        <div className="text-xs text-slate-500">ΑΦΜ</div>
                        <div className="text-slate-900 font-medium">{customer.tax_id}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Business Info */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-semibold text-slate-700 mb-4">
                  {customer.customer_type === 'existing' ? 'Στοιχεία Επιχείρησης' : 'Στοιχεία Υπό Σύσταση'}
                </h3>
                <div className="space-y-3">
                  {customer.customer_type === 'existing' ? (
                    <>
                      {customer.business_category && (
                        <div>
                          <div className="text-xs text-slate-500">Κατηγορία</div>
                          <div className="text-slate-900">{customer.business_category}</div>
                        </div>
                      )}
                      {customer.start_date && (
                        <div>
                          <div className="text-xs text-slate-500">Ημ. Έναρξης</div>
                          <div className="text-slate-900">{formatDate(customer.start_date)}</div>
                        </div>
                      )}
                      {customer.eme && (
                        <div>
                          <div className="text-xs text-slate-500">ΕΜΕ</div>
                          <div className="text-slate-900">{customer.eme}</div>
                        </div>
                      )}
                      {customer.annual_revenue && (
                        <div>
                          <div className="text-xs text-slate-500">Ετήσιος Τζίρος</div>
                          <div className="text-slate-900">{customer.annual_revenue.toLocaleString('el-GR')} €</div>
                        </div>
                      )}
                      {customer.kad && (
                        <div>
                          <div className="text-xs text-slate-500">ΚΑΔ Δραστηριότητας</div>
                          <div className="text-slate-900">{customer.kad}</div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {customer.planned_start_date && (
                        <div>
                          <div className="text-xs text-slate-500">Προγραμματισμένη Έναρξη</div>
                          <div className="text-slate-900">{formatDate(customer.planned_start_date)}</div>
                        </div>
                      )}
                      {customer.estimated_budget && (
                        <div>
                          <div className="text-xs text-slate-500">Εκτιμώμενος Προϋπολογισμός</div>
                          <div className="text-slate-900">{customer.estimated_budget.toLocaleString('el-GR')} €</div>
                        </div>
                      )}
                      {customer.business_idea && (
                        <div>
                          <div className="text-xs text-slate-500">Επιχειρηματική Ιδέα</div>
                          <div className="text-slate-900">{customer.business_idea}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* KAD File Section */}
              {customer.customer_type === 'existing' && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Αρχείο ΚΑΔ
                  </h4>
                  
                  {kadFile ? (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <span className="text-sm text-slate-700">{kadFile.filename}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleKadDownload}
                          data-testid="download-kad-btn"
                        >
                          <Upload className="h-4 w-4 mr-1 rotate-180" />
                          Λήψη
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600"
                          onClick={handleKadDelete}
                          data-testid="delete-kad-btn"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                      <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500 mb-2">Δεν υπάρχει αρχείο ΚΑΔ</p>
                      <label className="cursor-pointer">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild 
                          disabled={kadUploading}
                        >
                          <span>
                            <Upload className="h-4 w-4 mr-1" />
                            {kadUploading ? 'Ανέβασμα...' : 'Ανέβασμα αρχείου'}
                          </span>
                        </Button>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleKadUpload}
                          accept=".pdf,.doc,.docx,.xls,.xlsx"
                          data-testid="kad-file-input"
                        />
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Program Interests */}
            {customer.program_names?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-semibold text-slate-700 mb-3">Προγράμματα Ενδιαφέροντος</h3>
                <div className="flex flex-wrap gap-2">
                  {customer.program_names.map((name, idx) => (
                    <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {customer.notes && (
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-semibold text-slate-700 mb-3">Σημειώσεις</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{customer.notes}</p>
              </div>
            )}

            {/* Collaborators */}
            <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="collaborators-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-700">Συνεργάτες Πελάτη</h3>
                <Button size="sm" variant="outline" onClick={() => openCollabDialog()} data-testid="add-collab-btn">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Προσθήκη
                </Button>
              </div>
              {(!customer.collaborators || customer.collaborators.length === 0) ? (
                <p className="text-slate-400 text-sm">Δεν υπάρχουν συνεργάτες</p>
              ) : (
                <div className="space-y-2">
                  {customer.collaborators.map((collab, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`collab-row-${idx}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600 shrink-0">
                          {collab.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 text-sm">{collab.name}</div>
                          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                            {collab.role && <span>{collab.role}</span>}
                            {collab.phone && <span className="flex items-center gap-0.5"><Phone className="h-3 w-3" />{collab.phone}</span>}
                            {collab.email && <span className="flex items-center gap-0.5"><Mail className="h-3 w-3" />{collab.email}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => openCollabDialog(idx)} data-testid={`edit-collab-${idx}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => handleDeleteCollaborator(idx)} data-testid={`delete-collab-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-500">
              <div className="flex flex-wrap gap-4">
                <div>Δημιουργήθηκε: {formatDateTime(customer.created_at)}</div>
                {customer.created_by_name && <div>Από: {customer.created_by_name}</div>}
                {customer.updated_at !== customer.created_at && (
                  <div>Τελ. Ενημέρωση: {formatDateTime(customer.updated_at)}</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-700">Ιστορικό Επικοινωνιών</h3>
                <Button
                  size="sm"
                  onClick={openNewCommunicationDialog}
                  data-testid="add-communication-inline"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Νέα Επικοινωνία
                </Button>
              </div>
              
              {communications.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Δεν υπάρχουν καταχωρημένες επικοινωνίες
                </div>
              ) : (
                <div className="divide-y">
                  {communications.map(comm => (
                    <div key={comm.communication_id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${
                            comm.communication_type === 'call' ? 'bg-blue-100 text-blue-700' :
                            comm.communication_type === 'meeting' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {comm.communication_type === 'call' ? <Phone className="h-4 w-4" /> :
                             comm.communication_type === 'meeting' ? <Calendar className="h-4 w-4" /> :
                             <Mail className="h-4 w-4" />}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">
                              {comm.communication_type === 'call' ? 'Τηλεφωνική Κλήση' :
                               comm.communication_type === 'meeting' ? 'Συνάντηση' : 'Email'}
                            </div>
                            <div className="text-sm text-slate-600 mt-1">{comm.outcome}</div>
                            {comm.notes && (
                              <div className="text-sm text-slate-500 mt-2 p-2 bg-slate-50 rounded">
                                {comm.notes}
                              </div>
                            )}
                            {/* New fields display */}
                            {(comm.estimated_budget || comm.business_idea || comm.program_name) && (
                              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                                {comm.program_name && (
                                  <div className="inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-medium">
                                    {comm.program_name}
                                  </div>
                                )}
                                {comm.estimated_budget && (
                                  <div className="flex items-center gap-1 text-amber-700">
                                    <Euro className="h-3 w-3" />
                                    Προϋπ: {comm.estimated_budget.toLocaleString('el-GR')} €
                                  </div>
                                )}
                                {comm.business_idea && (
                                  <div className="flex items-center gap-1 text-emerald-700">
                                    <Lightbulb className="h-3 w-3" />
                                    Ιδέα: {comm.business_idea.length > 50 ? comm.business_idea.substring(0, 50) + '...' : comm.business_idea}
                                  </div>
                                )}
                              </div>
                            )}
                            {comm.next_contact_date && (
                              <div className="text-sm text-orange-600 mt-2 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Επόμενη επικοινωνία: {formatDate(comm.next_contact_date)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm text-slate-500">{formatDateTime(comm.created_at)}</div>
                          <div className="text-xs text-slate-400">{comm.employee_name}</div>
                          <div className="flex gap-1 mt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditCommunicationDialog(comm)}
                              data-testid={`edit-comm-${comm.communication_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {user.role === 'owner' && teamMembers.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600"
                                onClick={() => openAssignDialog(comm)}
                                data-testid={`assign-comm-${comm.communication_id}`}
                                title="Ανάθεση σε υπάλληλο"
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-700">Αρχεία Πελάτη</h3>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <Button size="sm" disabled={uploading} asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-1" />
                      {uploading ? 'Ανέβασμα...' : 'Ανέβασμα Αρχείου'}
                    </span>
                  </Button>
                </label>
              </div>
              
              {files.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Δεν υπάρχουν αρχεία
                </div>
              ) : (
                <div className="divide-y">
                  {files.map(file => (
                    <div key={file.file_id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-900">{file.original_filename}</div>
                          <div className="text-xs text-slate-500">
                            {file.uploaded_by_name} • {formatDateTime(file.uploaded_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => handleDownloadFile(file.file_id, file.original_filename)}
                          data-testid={`download-file-${file.file_id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteFile(file.file_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Communication Dialog (Create/Edit) */}
      <Dialog open={commDialogOpen} onOpenChange={setCommDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingComm ? 'Επεξεργασία Επικοινωνίας' : 'Νέα Επικοινωνία'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveCommunication} className="space-y-4">
            <div>
              <Label>Τύπος Επικοινωνίας</Label>
              <Select
                value={commForm.communication_type}
                onValueChange={(v) => setCommForm(prev => ({ ...prev, communication_type: v }))}
              >
                <SelectTrigger data-testid="comm-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Τηλεφωνική Κλήση</SelectItem>
                  <SelectItem value="meeting">Συνάντηση</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Αποτέλεσμα *</Label>
              <Input
                value={commForm.outcome}
                onChange={(e) => setCommForm(prev => ({ ...prev, outcome: e.target.value }))}
                placeholder="π.χ. Ενδιαφέρεται για το πρόγραμμα ψηφιακού μετασχηματισμού"
                required
                data-testid="comm-outcome-input"
              />
            </div>
            
            <div>
              <Label>Σημειώσεις</Label>
              <Textarea
                value={commForm.notes}
                onChange={(e) => setCommForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                data-testid="comm-notes-input"
              />
            </div>

            {/* Program of Interest */}
            {programs.length > 0 && (
              <div>
                <Label>Πρόγραμμα Ενδιαφέροντος</Label>
                <Select
                  value={commForm.program_interest || ''}
                  onValueChange={(v) => setCommForm(prev => ({ ...prev, program_interest: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="comm-program-select">
                    <SelectValue placeholder="Επιλέξτε πρόγραμμα..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Κανένα --</SelectItem>
                    {programs.map(p => (
                      <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* New fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Εκτιμώμενος Προϋπολογισμός (€)</Label>
                <Input
                  type="number"
                  value={commForm.estimated_budget}
                  onChange={(e) => setCommForm(prev => ({ ...prev, estimated_budget: e.target.value }))}
                  placeholder="π.χ. 50000"
                  data-testid="comm-budget-input"
                />
              </div>
              <div>
                <Label>Ημερομηνία Ραντεβού *</Label>
                <Input
                  type="date"
                  value={commForm.next_contact_date?.split('T')[0] || ''}
                  required={commForm.interest_status !== 'not_interested'}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = commForm.next_contact_time || '10:00';
                    setCommForm(prev => ({ ...prev, next_contact_date: date ? `${date}T${time}` : '', next_contact_time: time }));
                  }}
                  data-testid="comm-next-date-input"
                />
              </div>
              <div>
                <Label>Ώρα</Label>
                <Select
                  value={commForm.next_contact_time || '10:00'}
                  onValueChange={(v) => {
                    const date = commForm.next_contact_date?.split('T')[0] || '';
                    setCommForm(prev => ({ ...prev, next_contact_time: v, next_contact_date: date ? `${date}T${v}` : '' }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ώρα" />
                  </SelectTrigger>
                  <SelectContent>
                    {['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00','18:30','19:00','19:30','20:00'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Επιχειρηματική Ιδέα</Label>
              <Textarea
                value={commForm.business_idea}
                onChange={(e) => setCommForm(prev => ({ ...prev, business_idea: e.target.value }))}
                rows={2}
                placeholder="Περιγραφή ιδέας ή σχεδίου..."
                data-testid="comm-idea-input"
              />
            </div>
            
            {!editingComm && commForm.next_contact_date && (
              <p className="text-xs text-slate-500">
                Θα δημιουργηθεί αυτόματα ραντεβού
              </p>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCommDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit" data-testid="save-communication-button">
                {editingComm ? 'Ενημέρωση' : 'Καταχώρηση'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Επεξεργασία Πελάτη</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateCustomer} className="space-y-4">
            <div>
              <Label>Τύπος Πελάτη</Label>
              <Select
                value={editForm.customer_type}
                onValueChange={(v) => setEditForm(prev => ({ ...prev, customer_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing">Υφιστάμενη Επιχείρηση</SelectItem>
                  <SelectItem value="new">Υπό Σύσταση Επιχείρηση</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Επωνυμία *</Label>
                <Input
                  value={editForm.company_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, company_name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Υπεύθυνος</Label>
                <Input
                  value={editForm.contact_person || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, contact_person: e.target.value }))}
                />
              </div>
              <div>
                <Label>ΑΦΜ (9 ψηφία)</Label>
                <Input
                  value={editForm.tax_id || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, tax_id: validateTaxId(e.target.value) }))}
                  maxLength={9}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div>
                <Label>Τηλέφωνο</Label>
                <Input
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div>
                <Label>Κινητό</Label>
                <Input
                  value={editForm.mobile || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, mobile: e.target.value }))}
                />
              </div>
              <div className="relative">
                <Label>Περιφέρεια</Label>
                <Input
                  value={regionSearch}
                  onChange={(e) => {
                    setRegionSearch(e.target.value);
                    setShowRegionSuggestions(true);
                  }}
                  onFocus={() => setShowRegionSuggestions(true)}
                  placeholder="Πληκτρολογήστε..."
                />
                {showRegionSuggestions && filteredRegions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredRegions.map(region => (
                      <button
                        key={region.region_id}
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-slate-100"
                        onClick={() => selectRegion(region)}
                      >
                        {region.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Διεύθυνση</Label>
              <Input
                value={editForm.address || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>

            {editForm.customer_type === 'existing' && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-700 mb-3">Στοιχεία Υφιστάμενης</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Κατηγορία</Label>
                    <Select
                      value={editForm.business_category || ''}
                      onValueChange={(v) => setEditForm(prev => ({ ...prev, business_category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Επιλέξτε" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.category_id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Ημ. Έναρξης</Label>
                    <Input
                      type="date"
                      value={editForm.start_date || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, start_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>ΕΜΕ</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editForm.eme || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, eme: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Ετήσιος Τζίρος</Label>
                    <Input
                      type="number"
                      value={editForm.annual_revenue || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, annual_revenue: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>ΚΑΔ Δραστηριότητας</Label>
                    <Input
                      value={editForm.kad || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, kad: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {editForm.customer_type === 'new' && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-700 mb-3">Στοιχεία Υπό Σύσταση</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Προγρ. Έναρξη</Label>
                    <Input
                      type="date"
                      value={editForm.planned_start_date || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, planned_start_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Εκτιμ. Προϋπολογισμός</Label>
                    <Input
                      type="number"
                      value={editForm.estimated_budget || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, estimated_budget: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Επιχ. Ιδέα</Label>
                    <Textarea
                      value={editForm.business_idea || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, business_idea: e.target.value }))}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <Label className="mb-2 block">Προγράμματα Ενδιαφέροντος</Label>
              <div className="flex flex-wrap gap-2">
                {programs.map(program => (
                  <button
                    key={program.program_id}
                    type="button"
                    onClick={() => toggleProgramInterest(program.program_id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      editForm.program_interests?.includes(program.program_id)
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {program.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Σημειώσεις</Label>
              <Textarea
                value={editForm.notes || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit">Αποθήκευση</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Communication Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ανάθεση Επικοινωνίας</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Επιλέξτε υπάλληλο για να αναθέσετε αυτή την επικοινωνία (και το συνδεδεμένο ραντεβού, αν υπάρχει).
            </p>
            
            <div>
              <Label>Υπάλληλος</Label>
              <Select
                value={selectedAssignee}
                onValueChange={setSelectedAssignee}
              >
                <SelectTrigger data-testid="assignee-select">
                  <SelectValue placeholder="Επιλέξτε υπάλληλο" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={user.user_id}>Εγώ ({user.email})</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.role_name || member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button 
                onClick={handleAssignCommunication}
                disabled={!selectedAssignee}
                data-testid="confirm-assign-btn"
              >
                Ανάθεση
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Collaborator Dialog */}
      <Dialog open={collabDialogOpen} onOpenChange={(open) => { setCollabDialogOpen(open); if (!open) { setCollabForm({ name: '', role: '', phone: '', email: '' }); setEditingCollabIdx(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCollabIdx !== null ? 'Επεξεργασία Συνεργάτη' : 'Νέος Συνεργάτης Πελάτη'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="collab-dialog-form">
            <div className="space-y-2">
              <Label>Ονοματεπώνυμο *</Label>
              <Input value={collabForm.name} onChange={(e) => setCollabForm({ ...collabForm, name: e.target.value })} placeholder="π.χ. Γιώργος Παπαδόπουλος" data-testid="collab-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Ρόλος</Label>
              <Input value={collabForm.role} onChange={(e) => setCollabForm({ ...collabForm, role: e.target.value })} placeholder="π.χ. Λογιστής, Δικηγόρος" data-testid="collab-role-input" />
            </div>
            <div className="space-y-2">
              <Label>Τηλέφωνο</Label>
              <Input value={collabForm.phone} onChange={(e) => setCollabForm({ ...collabForm, phone: e.target.value })} placeholder="π.χ. 6912345678" data-testid="collab-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={collabForm.email} onChange={(e) => setCollabForm({ ...collabForm, email: e.target.value })} placeholder="π.χ. info@example.gr" data-testid="collab-email-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setCollabDialogOpen(false)} className="flex-1">Ακύρωση</Button>
              <Button onClick={handleSaveCollaborator} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="collab-submit-btn">
                {editingCollabIdx !== null ? 'Αποθήκευση' : 'Προσθήκη'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMCustomerDetail;
